// src/prototype/roi/hubFigures.ts
// ============================================================================
// The financial figures the Use-Case Hub shows (header KPI row, Brief-tab
// Financial Model card, compact strip). All figures describe the BASE scenario. One resolver, engine-sourced: every
// string here is formatted from roiForUseCase(id) with the engine's own
// formatters, so no hub surface holds a dollar amount, payback or confidence.
//
// roiForUseCase always yields a model (it falls back to canon/engine defaults
// internally), so the hub has no fixture fallback for these figures.
// ============================================================================

import { roiForUseCase } from './useCaseRoi';
import { formatMonths, formatUsd, formatUsdRange } from './roiEngine';

export interface HubFigures {
  /** 'Payback' — '3 months', or the engine's not-recovered string. */
  payback: string;
  /** 'Net 3-Year Value' — the engine's netValue3Yr, base scenario. */
  netValue3Yr: string;
  /** BASE-scenario annual value low → high, '$15.2M – $18.6M' — same scenario as payback and net value. */
  annualBenefitRange: string;
  /** 'Total 3-year cost' — implementation + 3 × operating. Never "implementation cost". */
  totalCost3Yr: string;
  /** Base-scenario confidence, '87%'. Always shown with CANON_CONFIDENCE_BASIS. */
  confidence: string;
}

export function hubFigures(useCaseId: string): HubFigures {
  const model = roiForUseCase(useCaseId);
  const base = model.scenarios.base;
  return {
    payback: formatMonths(base.paybackMonths),
    netValue3Yr: formatUsd(base.netValue3Yr),
    annualBenefitRange: formatUsdRange(base.annualValueLow, base.annualValueHigh),
    totalCost3Yr: formatUsd(base.totalCost3Yr),
    confidence: `${base.confidencePct}%`,
  };
}

/**
 * Base-scenario annual value low/high in $M, as numbers. The Explorer's filter, sort and
 * portfolio tally use these so they agree with the range the row and the hub header render.
 */
export function baseAnnualValueMillions(useCaseId: string): { low: number; high: number } {
  const base = roiForUseCase(useCaseId).scenarios.base;
  return { low: base.annualValueLow / 1_000_000, high: base.annualValueHigh / 1_000_000 };
}

/** Compact Base Case range for list rows, '$15.2–18.6M'; falls back to the full form below $1M. */
export function annualBenefitShort(useCaseId: string): string {
  const base = roiForUseCase(useCaseId).scenarios.base;
  if (base.annualValueLow >= 1_000_000 && base.annualValueHigh >= 1_000_000) {
    return `$${(base.annualValueLow / 1e6).toFixed(1)}–${(base.annualValueHigh / 1e6).toFixed(1)}M`;
  }
  return formatUsdRange(base.annualValueLow, base.annualValueHigh);
}

/** Base Case annual value as the opportunity fixture shape ({low, high} in raw dollars). */
export function baseRoiEstimate(useCaseId: string): { low: number; high: number } {
  const base = roiForUseCase(useCaseId).scenarios.base;
  return { low: base.annualValueLow, high: base.annualValueHigh };
}

/** Sum of Base Case low/high in $M over a set of use cases. THE portfolio tally: the Explorer
 *  header and the Portfolio Strategy report both call this so they cannot disagree. */
export function sumBaseAnnualValueMillions(ids: readonly string[]): { low: number; high: number } {
  let low = 0;
  let high = 0;
  for (const id of ids) {
    const m = baseAnnualValueMillions(id);
    low += m.low;
    high += m.high;
  }
  return { low, high };
}

/** '$15.2M – $18.6M' from $M numbers, the report's house format. */
export function formatMillionsRange(low: number, high: number): string {
  return `$${low.toFixed(1)}M – $${high.toFixed(1)}M`;
}

export type ReportScenario = 'conservative' | 'base' | 'aggressive';

/** One initiative's annual value low/high in $M under an engine scenario. */
export function scenarioAnnualValueMillions(
  useCaseId: string,
  scenario: ReportScenario,
): { low: number; high: number } {
  const sc = roiForUseCase(useCaseId).scenarios[scenario];
  return { low: sc.annualValueLow / 1_000_000, high: sc.annualValueHigh / 1_000_000 };
}

/**
 * Year-1 benefit, cost and net in $M for a portfolio under an engine scenario. All three come
 * from the same period of the same scenario (year1RealizedValue, year1Cost, year1NetValue), so
 * a waterfall built from them never mixes periods.
 */
export function sumScenarioYear1Millions(
  ids: readonly string[],
  scenario: ReportScenario,
): { benefit: number; cost: number; net: number } {
  let benefit = 0;
  let cost = 0;
  let net = 0;
  for (const id of ids) {
    const sc = roiForUseCase(id).scenarios[scenario];
    benefit += sc.year1RealizedValue / 1_000_000;
    cost += sc.year1Cost / 1_000_000;
    net += sc.year1NetValue / 1_000_000;
  }
  return { benefit, cost, net };
}
